import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import {
  DotsIcon,
  FilePdfIcon,
  FileCheckIcon,
  FileAndEmailIcon,
  HexagonArrowIcon,
  BackCurvedArrowIcon,
} from '@dynamika-shared/ui/icons';
import { getValueWithTooltip } from '@dynamika-shared/ui/Table/getValueWithTooltip';
import {
  ClinicalTrialReport,
  ReportReviewStatus,
} from '@restore/clinicalTrialReports/clinicalTrialReports.types';
import { Dropdown } from '@dynamika-shared/ui/Dropdown/Dropdown';
import { format } from 'date-fns';
import { Tooltip } from 'antd';
import { ReactNode } from 'react';
import { Procedure } from '../../../components/modals/ReportProcedureConfirmation/ReportProcedureConfirmation';

type ReportColumnType = {
  action: (report: ClinicalTrialReport, procedure: Procedure) => void;
};
type ColumnDropdownItem = {
  key: Procedure;
  label: string | ReactNode;
  showWhenStatus: ReportReviewStatus[];
  icon: ReactNode;
  disabled?: boolean;
};

const LENGTH_FOR_TOOLTIP = 10;

const loading = () => (
  <div className='h-2 w-10 animate-pulse rounded-md bg-brightGray'></div>
);

const dropdownItems: ColumnDropdownItem[] = [
  {
    key: 'pdf',
    label: <div>View PDF</div>,
    showWhenStatus: ['NEW', 'REJECTED', 'APPROVED'],
    icon: (
      <FilePdfIcon
        width={18}
        height={18}
        className={'ml-[.4rem] pr-[.4rem] text-black'}
      />
    ),
  },
  {
    key: 'release',
    label: <div>Release Report</div>,
    showWhenStatus: ['NEW'],
    icon: (
      <FileCheckIcon
        width={18}
        height={18}
        className={'ml-[.4rem] pr-[.4rem] text-black'}
      />
    ),
  },
  {
    key: 'reject',
    label: <div>Reject Report</div>,
    showWhenStatus: ['NEW'],
    icon: (
      <BackCurvedArrowIcon
        width={18}
        height={18}
        className={'ml-[.4rem] pr-[.4rem] text-black'}
      />
    ),
  },
  {
    key: 'release_and_email',
    label: <div>Release and Email</div>,
    showWhenStatus: ['NEW'],
    icon: (
      <FileAndEmailIcon
        width={18}
        height={18}
        className={'ml-[.4rem] pr-[.4rem] text-black'}
      />
    ),
    disabled: false,
  },
  {
    key: 'email',
    label: <div>Email Report</div>,
    showWhenStatus: ['APPROVED'],
    icon: (
      <FileAndEmailIcon
        width={18}
        height={18}
        className={'ml-[.4rem] pr-[.4rem] text-black'}
      />
    ),
    disabled: true,
  },
  {
    key: 'send',
    label: <div>Send Back</div>,
    showWhenStatus: ['NEW', 'APPROVED'],
    icon: (
      <BackCurvedArrowIcon
        width={18}
        height={18}
        className={'ml-[.4rem] pr-[.4rem] text-black'}
      />
    ),
    disabled: true,
  },
  {
    key: 'escalate',
    label: <div>Escalate</div>,
    showWhenStatus: ['NEW', 'APPROVED'],
    icon: (
      <HexagonArrowIcon
        width={18}
        height={18}
        className={'ml-[.4rem] pr-[.4rem] text-black'}
      />
    ),
    disabled: true,
  },
];

export const makeReportColumns = (
  props: ReportColumnType
): ColumnType<ClinicalTrialReport>[] => {
  return [
    {
      name: 'patientCode',
      header: () => 'Subject ID',
      loading,
      getValue: getValueWithTooltip('patientCode', LENGTH_FOR_TOOLTIP),
      hasSorting: true,
      thClassName: 'w-[15%]',
      headerClassName: 'whitespace-nowrap',
    },
    {
      name: 'visitName',
      header: () => 'Visit',
      loading,
      getValue: getValueWithTooltip('visitName', LENGTH_FOR_TOOLTIP),
      hasSorting: true,
      thClassName: 'w-[15%]',
      headerClassName: 'whitespace-nowrap',
    },

    {
      name: 'createdDate',
      header: () => 'Report Date',
      loading,
      getValue: row => {
        return format(new Date(row.createdDate), 'dd-MMM-yyy');
      },
      hasSorting: true,
      thClassName: 'w-[15%]',
      headerClassName: 'whitespace-nowrap',
    },
    {
      name: 'reportName',
      header: () => 'Report Name',
      loading,
      getValue: getValueWithTooltip('reportName', LENGTH_FOR_TOOLTIP),
      thClassName: 'w-[20%]',
      headerClassName: 'whitespace-nowrap',
    },
    {
      name: 'assigneeIds',
      header: () => 'Reader',
      loading,
      getValue: row => {
        const readers = row?.readers?.join(', ');
        const text = readers?.length ? readers : 'n/a';
        return (
          <Tooltip
            color='white'
            title={
              text?.length < LENGTH_FOR_TOOLTIP ? (
                ''
              ) : (
                <span className='text-1424 text-black'>{text}</span>
              )
            }
          >
            <div className='max-w-full truncate bg-transparent'>{text}</div>
          </Tooltip>
        );
      },
      headerClassName: 'whitespace-nowrap',
    },
    {
      name: 'action',
      header: () => 'Action',
      loading: () => (
        <div className='h-3 w-3 animate-pulse rounded-md bg-brightGray'></div>
      ),
      getValue: report => {
        return (
          <div className='ml-1 flex items-center justify-center'>
            <Dropdown
              items={dropdownItems.filter(item =>
                item.showWhenStatus.includes(report.reviewStatus)
              )}
              overlayClassName={'w-[20rem]'}
              trigger={['click']}
              onClick={e => props.action(report, e.key as Procedure)}
            >
              <div
                className='flex h-5 cursor-pointer items-center justify-center'
                data-tesitid={`menuReport-${report.id}`}
              >
                <div className='rounded-3xl p-1 hover:bg-waterLight'>
                  <DotsIcon className='hover:text-black' />
                </div>
              </div>
            </Dropdown>
          </div>
        );
      },
      alignToRight: true,
      thClassName: 'w-[7rem] min-w-[5%]',
    },
  ];
};
